import type {
  NewHomeworkAiSpeechHistoryResponse,
  NewHomeworkAiSpeechItemResponse,
  NewHomeworkAnswerInfoParams,
  NewHomeworkConversationAnswerInfoResponse,
  NewHomeworkConversationListResponse,
  NewHomeworkCreateConversationBody,
  NewHomeworkCreateWritingBody,
  NewHomeworkCreateWritingResponse,
  NewHomeworkListFilterThemeParams,
  NewHomeworkRestartConversationBody,
  NewHomeworkRestartSpeechBody,
  NewHomeworkRestartSpeechResponse,
  NewHomeworkRestartWritingBody,
  NewHomeworkRestartWritingResponse,
  NewHomeworkSendAiSpeechDetailBody,
  NewHomeworkSendAiSpeechDetailResponse,
  NewHomeworkSendConversationResultResponse,
  NewHomeworkSpeechDetailParams,
  NewHomeworkSpeechDetailResponse,
  NewHomeworkTranslateToTCBody,
  NewHomeworkWritingAnswerInfoResponse,
  NewHomeworkWritingHistoryResponse,
  NewHomeworkWritingListResponse
} from '@/models/interface/newHomework';
import { ApiFactory, type ApiDataResponse } from '@/utils/api';

export class NewHomeworkService extends ApiFactory {
  constructor() {
    super('HomeWork');
  }

  /**
   * 取得 寫作作業列表
   * @param params.themeId - 課程主題 ID (學習目標)
   */
  getNewHomeworkWritingList(
    params: NewHomeworkListFilterThemeParams
  ): Promise<ApiDataResponse<NewHomeworkWritingListResponse[]>> {
    return super.get(`WritingHomeworkList`, params, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 取得 寫作作業答案資訊
   * @param params.id - 單項作業 ID
   */
  getNewHomeworkWritingAnswerInfo(
    params: NewHomeworkAnswerInfoParams
  ): Promise<ApiDataResponse<NewHomeworkWritingAnswerInfoResponse>> {
    return super.get(`WritingHomeworkAnswerInfo`, params, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 取得 寫作作業歷史紀錄
   */
  getNewHomeworkWritingHistory(): Promise<ApiDataResponse<NewHomeworkWritingHistoryResponse[]>> {
    return super.get(`WritingHomeworkHistory`, null, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 建立 寫作作業答案
   * @param body - 寫作作業請求酬載
   */
  createWritingHomework(
    body: NewHomeworkCreateWritingBody
  ): Promise<ApiDataResponse<NewHomeworkCreateWritingResponse>> {
    return super.post(`SaveWritingHomework`, body, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 重新開始 寫作作業作答資訊
   * @param body - 寫作作業作答請求酬載
   */
  restartNewHomeworkWriting(
    body: NewHomeworkRestartWritingBody
  ): Promise<ApiDataResponse<NewHomeworkRestartWritingResponse>> {
    return super.post(`RestartWritingHomework`, body, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 取得 情境對話列表
   * @param params.themeId - 課程主題 ID (學習目標)
   */
  getNewHomeworkConversationList(
    params: NewHomeworkListFilterThemeParams
  ): Promise<ApiDataResponse<NewHomeworkConversationListResponse[]>> {
    return super.post(`ConversationList`, null, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      },
      params: { themeId: params.themeId }
    });
  }
  /**
   * 取得 指定情境對話資訊
   * @param params.id - 單一情境對話資訊 ID
   */
  getNewHomeworkConversationAnswer(
    params: NewHomeworkAnswerInfoParams
  ): Promise<ApiDataResponse<NewHomeworkConversationAnswerInfoResponse>> {
    return super.get(`ConversationAnswer`, params, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      },
      timeout: 30000
    });
  }
  /**
   * 取得 情境對話歷史紀錄
   */
  getNewHomeworkConversationHistory(): Promise<
    ApiDataResponse<NewHomeworkConversationListResponse[]>
  > {
    return super.get(`ConversationHistory`, null, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 建立 情境對話訊息
   * @param body - 對話訊息請求酬載
   * @remarks 若為最後一個對話，會回傳評分結果
   */
  createNewHomeworkConversation(
    body: NewHomeworkCreateConversationBody
  ): Promise<ApiDataResponse<NewHomeworkSendConversationResultResponse>> {
    return super.post(`SendConversation`, body, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      },
      timeout: 100000
    });
  }
  /**
   * 重新開始 指定情境對話
   * @param body.conversationId - 單一情境對話代碼
   */
  restartNewHomeworkConversation(
    body: NewHomeworkRestartConversationBody
  ): Promise<ApiDataResponse<undefined>> {
    return super.post(`RestartConversation`, body, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      },
      timeout: 100000
    });
  }
  /**
   * 對話訊息英翻中
   * @param body.text - 需翻譯的文字
   */
  translateNewHomeworkConversationToTC(
    body: NewHomeworkTranslateToTCBody
  ): Promise<ApiDataResponse<string>> {
    return super.post(`TranslateToTC`, body, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      },
      timeout: 100000
    });
  }
  /**
   * 取得 隨機朗讀作業項目
   * @remarks 依照等級，隨機取得尚未完成的朗讀作業
   */
  getNewHomeworkAiSpeechItem(): Promise<ApiDataResponse<NewHomeworkAiSpeechItemResponse>> {
    return super.get(`AiSpeechHomework`, null, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 取得 朗讀作業歷史紀錄
   * @remarks AI 評分「已完成」、「批改中」的朗讀作業
   */
  getNewHomeworkAiSpeechHistory(): Promise<ApiDataResponse<NewHomeworkAiSpeechHistoryResponse[]>> {
    return super.get(`AiSpeechHomeworkHistory`, null, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true
      }
    });
  }
  /**
   * 取得 朗讀作業作答資訊
   * @param params.articleId - 朗讀作業 ID
   */
  getNewHomeworkSpeechDetail(
    params: NewHomeworkSpeechDetailParams
  ): Promise<ApiDataResponse<NewHomeworkSpeechDetailResponse>> {
    return super.get(`SpeechHomeworkDetail`, params, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true,
        timeout: 30000
      }
    });
  }
  /**
   * 建立 朗讀作業作答資訊
   * @param body - 朗讀作業作答請求酬載
   */
  createNewHomeworkAiSpeech(
    body: NewHomeworkSendAiSpeechDetailBody
  ): Promise<ApiDataResponse<NewHomeworkSendAiSpeechDetailResponse>> {
    return super.post(`SendAiSpeechHomework`, body, {
      headers: {
        'Content-Type': 'multipart/form-data',
        disableGlobalLoading: true,
        timeout: 30000
      }
    });
  }
  /**
   * 重新開始 朗讀作業作答資訊
   * @param body - 朗讀作業作答請求酬載
   */
  restartNewHomeworkSpeech(
    body: NewHomeworkRestartSpeechBody
  ): Promise<ApiDataResponse<NewHomeworkRestartSpeechResponse>> {
    return super.post(`RestartSpeechHomework`, body, {
      headers: {
        'Content-Type': 'application/json',
        disableGlobalLoading: true,
        timeout: 30000
      }
    });
  }
}
